/**
 * 「這個失敗到底是哪一種」的**唯一分類處**：看**回應的 body**（`code`／`error`），不是只看 status。
 *
 * 為什麼要這一支：以前 `http-client.request()` 把所有 401 都翻成「登入已經過期，請重新登入」，
 * 但 401 至少有兩種完全不同的病：
 *   1. session 真的過期／被撤銷（`{code:'unauthenticated'}`，訊息裡沒有任何金鑰字樣）
 *      → 該說「請重新登入」。
 *   2. 缺的是**另一把鑰匙**：寫入類端點曾要 `x-api-token`（後端 `SERVICE_TOKEN`；
 *      2026-10-27 已從寫入路徑移除，現在只會出現在還開著舊閘門的部署）。後端回的是
 *      `401 { code: 'unauthenticated', message: '…X-API-TOKEN...' }`（`auth.ts` 的
 *      `requireServiceToken`，實測 code 為 `unauthorized` 時訊息同樣帶 `X-Api-Token`）
 *      → 說「請重新登入」是**錯的**：使用者剛登入，重登幾次都一樣，而且 UI 上根本沒地方設金鑰。
 *
 * 硬規則：**只有「body 沒有金鑰字樣、也沒有講權限」的 401 才算登入過期**。寧可說得保守，
 * 也不要把「缺設定」誤導成「請重新登入」——後者會讓使用者一直登出重登。
 *
 * 純函式、不碰 DOM、不碰 fetch：測試與 http-client 共用同一份判斷。
 */
/**
 * 後端講到「操作金鑰」的字樣。`requireServiceToken()` 的訊息是
 * 「未授權（缺少或錯誤的 X-Api-Token）」；不外乎這幾種寫法。
 */
const API_TOKEN_PATTERN = /x-api-token|api[-_\s]?token|操作金鑰|service[_-]?token/i;
/** 後端講到「權限」的字樣（403 常常只有這幾個字）。 */
const FORBIDDEN_TEXT = /forbidden|沒有權限|權限不足|不允許|僅限|需要管理/i;
/** 這些 code 一定是操作金鑰的問題（不管訊息寫什麼）。 */
const API_TOKEN_CODES = new Set(['service_token_required', 'api_token_required', 'api_token_missing']);
/** 這些 code 是 Admin Token 的問題。 */
const ADMIN_TOKEN_CODES = new Set(['admin_token_required', 'admin_token_missing', 'admin_unauthorized']);
export const API_TOKEN_NEEDED_MESSAGE = '需要操作金鑰（不是登入問題）：請到「操作金鑰」設定貼一次。';
export const SESSION_EXPIRED_MESSAGE = '登入已經過期，請重新登入。';
/** 這個訊息像不像「缺操作金鑰」？（給只有字串、沒有 Error 物件的地方用，例如 `useInbox.error`） */
export function isApiTokenMessage(message) {
    return API_TOKEN_PATTERN.test(message);
}
function detailOf(input) {
    const raw = input.error ?? '';
    const detail = input.detail ?? '';
    if (raw !== '' && detail !== '' && detail !== raw)
        return `${raw}（${detail}）`;
    if (detail !== '')
        return detail;
    if (raw !== '')
        return raw;
    return `${input.status} ${input.statusText ?? ''}`.trim();
}
/**
 * 一次失敗的回應 → 種類＋一句話。**順序就是優先序**（先看 code，再看訊息，最後才是狀態碼）。
 */
export function classifyApiFailure(input) {
    // 後端的 code 有時用中線（`admin-token-required`）、有時用底線（`admin_token_required`）；
    // 一律正規化成底線再查表，否則同一件事會只認得一半的寫法。
    const code = input.code === undefined ? '' : input.code.trim().replace(/-/g, '_');
    const message = `${input.error ?? ''} ${input.detail ?? ''}`;
    // ① 後端沒設 SERVICE_TOKEN（503 service_token_unset）：貼金鑰也救不了，必須講清楚。
    if (code === 'service_token_unset') {
        return {
            kind: 'api_token_unset',
            message: '寫入端點未開放：後端沒設 SERVICE_TOKEN（wrangler secret put SERVICE_TOKEN）——不是你的金鑰問題。',
            sessionExpired: false,
        };
    }
    // ② 缺操作金鑰：**這是最容易被誤認成「登入過期」的一種**，必須排在 session 前面。
    if (API_TOKEN_CODES.has(code) || API_TOKEN_PATTERN.test(message)) {
        return { kind: 'api_token', message: API_TOKEN_NEEDED_MESSAGE, sessionExpired: false };
    }
    // ③ Admin Token（系統頁的 X-Admin-Token）：也是「另一把鑰匙」，一樣不是重新登入。
    if (ADMIN_TOKEN_CODES.has(code) || /\bx-admin-token\b/i.test(message)) {
        return {
            kind: 'admin_token',
            message: `Admin Token 未通過（X-Admin-Token）：請在系統頁重新貼一次（不是登入問題）。${code === '' ? '' : `［${code}］`}`,
            sessionExpired: false,
        };
    }
    // ④ 握手成功但沒有這個權限。
    if (input.status === 403) {
        return {
            kind: 'forbidden',
            message: `這項操作需要管理權限（403）：${detailOf(input)}`,
            sessionExpired: false,
        };
    }
    // ⑤ 撞名：後端已自動換名重試仍失敗——翻成人看得懂的一句，不要把 HTTP 400 原文丟上畫面。
    if (code === 'nickname_taken' || message.includes('暱稱正在使用')) {
        return {
            kind: 'nickname_taken',
            message: `暱稱撞名，自動重試失敗，請再按一次建帳號（${detailOf(input)}）`,
            sessionExpired: false,
        };
    }
    // ⑥ 其餘 401：body 沒有金鑰字樣、也沒講權限 → 才是真的登入過期。
    if (input.status === 401) {
        // 但若 body 只有一個看不懂的自訂 code（`admin-token-required` 這種已在 ③ 接走），
        // 仍要把 code 帶給使用者，否則「按重試也沒用」時查不出原因。
        // 已知的兩個「就是登入」的 code 不加尾巴：那句話要保持乾淨（既有測試與畫面都吃這一句）。
        const known = code === '' || code === 'unauthenticated' || code === 'unauthorized';
        const suffix = known ? '' : `（${code}）`;
        return { kind: 'session', message: `${SESSION_EXPIRED_MESSAGE}${suffix}`, sessionExpired: true };
    }
    // ⑦ 其他：保留後端原文（呼叫端會自己決定要不要再包一層上下文）。
    return { kind: 'other', message: detailOf(input), sessionExpired: false };
}
/** 這個失敗是不是「權限類」（401／403）；`data-state` 判斷四態時用同一份定義。 */
export function isPermissionFailure(input) {
    const kind = classifyApiFailure(input).kind;
    return kind === 'api_token' || kind === 'admin_token' || kind === 'session' || kind === 'forbidden';
}
/** 這個訊息像不像「沒有權限」（403）？（給只有字串的地方用） */
export function isForbiddenMessage(message) {
    return FORBIDDEN_TEXT.test(message);
}
//# sourceMappingURL=error-copy.js.map